import React, { createContext, useContext, useMemo } from 'react';
import { Properties } from './logger';

export const LogContext = createContext(undefined);

type KnownProperties = Properties & {
  source?: string;
  componentName?: string;
  mfeName?: string;
};

interface LogContextProviderProps {
  children: React.ReactNode;
  source?: string;
  componentName?: string;
  mfeName?: string;
  tags?: Properties;
}

export const useLogContext = (): KnownProperties | undefined => {
  return useContext(LogContext);
};

const aggregateLogs = (
  parentContext: KnownProperties,
  { source, componentName, mfeName, ...rest }: KnownProperties
) => {
  const props =
    source || componentName
      ? {
          components: [
            ...(Array.isArray(parentContext?.components) ? parentContext.components : []),
            { source, componentName },
          ],
        }
      : {};

  return {
    ...parentContext,
    ...rest,
    mfeName: mfeName ?? parentContext?.mfeName,
    ...props,
  };
};

export const LogContextProvider = ({ children, componentName, source, mfeName, tags }: LogContextProviderProps) => {
  const parentContext = useLogContext();
  const ctx = useMemo(
    () => aggregateLogs(parentContext ?? {}, { ...tags, componentName, source, mfeName }),
    [parentContext, componentName, source, mfeName, tags]
  );

  return <LogContext.Provider value={ctx}>{children}</LogContext.Provider>;
};

export const getWrapperWithLogContext =
  (source: string) =>
  <T extends object>(Component: React.FC<T>, componentName: string) =>
  (props: T) => (
    <LogContextProvider componentName={componentName} source={source}>
      <Component {...props} />
    </LogContextProvider>
  );

export const clientLibComponentWrapper = getWrapperWithLogContext('client-lib');
